import React, { useEffect, useState } from "react";
import { Modal, Button, Typography, Spin, Space } from "antd";
import {
  formatSuppliesStatus,
  formatOrderDate,
  trackingSearchUrl,
} from "./suppliesOrderTracking";

const { Text, Paragraph } = Typography;
const BRAND = "#6DB33F";

/**
 * Simple popup showing the latest supplies order status + tracking.
 * Refreshes from Amazon via the check-status endpoint when opened.
 */
const SuppliesOrderTrackingModal = ({
  open,
  onClose,
  order,
  productLabel = "Order",
}) => {
  const [live, setLive] = useState(order);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLive(order);
  }, [order]);

  useEffect(() => {
    if (!open || !order?.stripePaymentIntentId) return;

    let cancelled = false;
    const refresh = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const isRolls = order.productType === "label_rolls";
        const path = isRolls
          ? `/api/payment/check-printer-rolls-status/${order.stripePaymentIntentId}`
          : `/api/payment/check-printer-status/${order.stripePaymentIntentId}`;
        const res = await fetch(path, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (cancelled) return;
        setLive((prev) => ({
          ...prev,
          status: data.status || prev?.status,
          amazonStatus: data.amazonStatus || prev?.amazonStatus,
          trackingNumber: data.trackingNumber || prev?.trackingNumber,
          trackingCarrier: data.trackingCarrier || prev?.trackingCarrier,
          failureReason: data.failureReason || prev?.failureReason,
        }));
      } catch {
        // keep listed order data
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    refresh();
    return () => {
      cancelled = true;
    };
  }, [open, order?.stripePaymentIntentId, order?.productType]);

  const addr = live?.shippingAddress || {};
  const placed = formatOrderDate(live?.createdAt);
  const trackUrl = trackingSearchUrl(
    live?.trackingNumber,
    live?.trackingCarrier
  );
  const qty = live?.lines?.[0]?.quantity;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Current ${productLabel}`}
      footer={[
        <Button key="close" type="primary" onClick={onClose} style={{ background: BRAND, borderColor: BRAND }}>
          Close
        </Button>,
      ]}
      destroyOnClose
    >
      {!live ? (
        <Text type="secondary">No order details available.</Text>
      ) : (
        <Spin spinning={loading}>
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <div>
              <Text type="secondary">Status</Text>
              <div>
                <Text strong style={{ fontSize: 16 }}>
                  {formatSuppliesStatus(live.status)}
                </Text>
                {live.amazonStatus && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    ({live.amazonStatus})
                  </Text>
                )}
              </div>
            </div>

            {placed && (
              <div>
                <Text type="secondary">Placed</Text>
                <div>
                  <Text>{placed}</Text>
                </div>
              </div>
            )}

            {live.productType === "label_rolls" && qty ? (
              <div>
                <Text type="secondary">Quantity</Text>
                <div>
                  <Text>
                    {qty} roll{qty === 1 ? "" : "s"}
                  </Text>
                </div>
              </div>
            ) : null}

            <div>
              <Text type="secondary">Delivery address</Text>
              <Paragraph style={{ marginBottom: 0 }}>
                {[addr.street, addr.city, addr.postalCode, addr.state]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </Paragraph>
            </div>

            <div>
              <Text type="secondary">Tracking</Text>
              {live.trackingNumber ? (
                <div>
                  {live.trackingCarrier && (
                    <Text style={{ display: "block" }}>
                      {live.trackingCarrier}
                    </Text>
                  )}
                  <Text strong copyable>
                    {live.trackingNumber}
                  </Text>
                  {trackUrl && (
                    <div style={{ marginTop: 6 }}>
                      <a href={trackUrl} target="_blank" rel="noreferrer">
                        Track parcel
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <Text type="secondary">
                    Tracking will appear here once Amazon ships your order.
                  </Text>
                </div>
              )}
            </div>

            {live.failureReason && (
              <Text type="danger">{live.failureReason}</Text>
            )}
          </Space>
        </Spin>
      )}
    </Modal>
  );
};

export default SuppliesOrderTrackingModal;
